import { Box, Grid, Pagination, Stack } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AppTransComponents } from "app/appTrans";
import { MprcChipChoice, MprcConfirmDialog, MprcContainer, MprcIconButtonMoreOptionsDropdown, MprcInfiniteLoading, MprcRequestErrorReport, MprcRequestIndicator, MprcText, MprcTextMuted } from "components";
import { ButtonAdd } from "components/buttons";
import { MprcFilterBox, MprcFilterLine } from "components/filter";
import { IconButtonAI } from "components/iconButtons";
import { IMenuItem, MprcButtonDropdown } from "components/MprcButtonDropdown";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcRibbonScroll } from "components/MprcRibbonScroll";
import { MprcSearchTextField, MprcSearchTextFieldHandle } from "components/MprcSearchTextField";
import { ManagedTopicChallengeRequest, ManagedTopicQuestionGroupDto, QuestionChallengeListResponse, QuestionChallengeListResponsePaginatedList, QuestionType } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { GeneratorQuestionAI } from "views/assessment/generatorQuestion/GeneratorQuestionAI";
import { QuestionVersionDrawer } from "views/assessment/questionBank/drawers/QuestionVersionDrawer";
import { EditQuestion } from "views/assessment/questionBank/Home/EditQuestion/EditQuestion";
import { EditTopicChallengeSelectQuestionsDialog } from "views/assessment/topicChallenge/Home/EditTopicChallenge/EditTopicChallengeSelectQuestionsDialog";
import { EditTopicChallengeViewQuestionsDialog } from "views/assessment/topicChallenge/Home/EditTopicChallenge/EditTopicChallengeViewQuestionsDialog";

////////////////////////////////////////////////////////////////////////////

type QuestionItemActionOptions = "duplicate" | "reactive" | "versions" | "remove";

type QuestionActionOptions = "newQuestion" | "selectQuestion" | "createAI";

interface IQuestionFilterOptions {
    searchText: string;
}

interface IViewDataFilter {
    pageNumber: number;
    filter: IQuestionFilterOptions;
}

////////////////////////////////////////////////////////////////////////////

interface IProps {
    viewData: ManagedTopicChallengeRequest;
    fullScreenMode: boolean;
    onUpdate: (viewData: ManagedTopicChallengeRequest) => void;
    refreshQuestionsInfo: (totalQuestions: number, questionsChanged: boolean) => void;
}

////////////////////////////////////////////////////////////////////////////

export const EditTopicChallengeQuestions: React.FC<IProps> = (props) => {

    const { viewData, refreshQuestionsInfo, fullScreenMode, onUpdate } = props;

    const questionGroupId = viewData.internalQuestionGroupId!;

    const api = useCoreApi();
    const { t } = useTranslation("assessment");
    const { enqueueSnackbar } = useSnackbar();

    const [dialog, setDialog] = useState<any>();
    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [copying, setCopying] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [questions, setQuestions] = useState<QuestionChallengeListResponsePaginatedList | null>(null);
    const searchTextField = useRef<MprcSearchTextFieldHandle | null>(null);
    const questionsChanged = useRef<boolean>(false);

    const viewFilter = useRef<IViewDataFilter>({
        pageNumber: 1,
        filter: { searchText: "" }
    });

    const hasPagination = (questions && questions.totalPages > 1) ?? false;
    const hasGroups = viewData.questionGroups.length > 0;

    ////////////////////////////////////////////////////////////////////////////

    ///
    const totalQuestions = () => {
        let result = questions?.totalRows ?? 0;

        if (viewData.questionGroups.length > 0) {
            result += viewData.questionGroups.map(x => x.totalQuestions).reduce((total, item) => total + item);
        }

        refreshQuestionsInfo(result, questionsChanged.current);
    };

    ///
    const handleEdit = (question: QuestionChallengeListResponse | null) => {
        const questionType = question == null ? QuestionType.SimpleChoice : question?.questionType;
        if (questionType) {
            setDialog(<EditQuestion
                questionId={question == null ? null : question.questionId}
                questionGroupId={questionGroupId}
                questionType={questionType}
                forSurvey={false}
                editMode="challenge"
                onClose={(success) => {
                    if (success) {
                        questionsChanged.current = true;
                        setLoading(true);
                    }
                    setDialog(null);
                }}
            />);
        }
    };

    ///
    const openGenerateQuestions = () => {
        setDialog(<GeneratorQuestionAI
            questionGroupId={questionGroupId}
            forSurvey={false}
            hasPoints={false}
            editMode="challenge"
            onClose={(success) => {
                if (success) {
                    questionsChanged.current = true;
                    setLoading(true);
                }
                setDialog(null);
            }}
        />);
    };

    ///
    const viewQuestions = (questionGroup: ManagedTopicQuestionGroupDto) => {
        setDialog(<EditTopicChallengeViewQuestionsDialog
            questionGroupId={questionGroup.questionGroupId}
            title={questionGroup.fullName}
            onClose={() => setDialog(null)}
        />);
    };

    ///
    async function copyQuestions(ids: string[]) {

        if (viewData.internalQuestionGroupId == null || ids.length == 0) return;

        setCopying(true);
        try {
            const result = await api.v1.questionBank.copyQuestionsToNewGroup({ questionGroupId: viewData.internalQuestionGroupId, questionIds: ids });

            if (result.data) {
                enqueueSnackbar(t("TopicChallenge.Edit.Question.CopyQuestionSuccessMessage"), { variant: "success" });
                questionsChanged.current = true;
                setLoading(true);
            }
        }
        catch (e) {
            MprcRequestSnackbarErrorReport(e);
        }
        finally {
            setCopying(false);
        }
    }

    // Duplicar o item
    const duplicateItem = (item: QuestionChallengeListResponse) => {
        setCopying(true);

        api.v1.questionBank.copyQuestionsToNewGroup({ questionGroupId, questionIds: [item.questionId] })
            .then(result => {
                if (result.data) {
                    questionsChanged.current = true;
                    setLoading(true);
                }
            })
            .catch(e => MprcRequestSnackbarErrorReport(e))
            .finally(() => setCopying(false));
    };

    ///
    const getQuestionMenuItems = useCallback((item: QuestionChallengeListResponse): MprcDropdownItems<QuestionItemActionOptions> => {
        const group0: MprcDropdownItems<QuestionItemActionOptions> = [];
        group0.push({ text: t("TopicChallenge.Edit.Question.DuplicateAction"), value: "duplicate" });
        group0.push({ text: t("TopicChallenge.Edit.Question.VersionsAction"), value: "versions" });

        const group1: MprcDropdownItems<QuestionItemActionOptions> = [];
        group1.push({ text: <MprcText color="error">{t("Actions.Delete", { ns: "shared" })}</MprcText>, value: "remove" });

        return MprcDropdownBuildGroupedItems<QuestionItemActionOptions>([group0, group1]);
    }, []);

    ///
    const getAddOptionsItems = useCallback((): Array<IMenuItem> => {
        var items: Array<IMenuItem> = [];

        items.push({ text: t("TopicChallenge.Edit.Question.NewQuestionAction"), value: "newQuestion" });
        items.push({ text: t("TopicChallenge.Edit.Question.SelectQuestionAction"), value: "selectQuestion" });
        items.push({ text: t("TopicChallenge.Edit.Question.CreateAIAction"), value: "createAI" });

        return items;
    }, []);

    ///
    const handleSelectQuestionOption = (option: QuestionItemActionOptions, question: QuestionChallengeListResponse, index: number) => {
        switch (option) {
            case "remove":
                setDialog(<MprcConfirmDialog
                    open
                    severity="danger"
                    message={<span>
                        <Trans
                            i18nKey="Question.List.ConfirmDeleteMessage"
                            values={{ name: question.questionText }}
                            components={AppTransComponents}
                            t={t}
                        />
                    </span>}
                    onClose={
                        (result) => {
                            if (result) {
                                setDeleteId(question.questionId);
                            }
                            setDialog(null);
                        }
                    }
                />);
                break;

            case "versions":
                setDialog(<QuestionVersionDrawer
                    questionId={question.questionId}
                    title={question.questionText}
                    onClose={() => setDialog(null)}
                />);
                break;

            case "duplicate":
                duplicateItem(question);
                break;
        }
    };

    ///
    const handleDeleteGroup = (questionGroup: ManagedTopicQuestionGroupDto) => {
        setDialog(<MprcConfirmDialog
            open
            severity="danger"
            message={<span>
                <Trans
                    i18nKey="TopicChallenge.Edit.Question.ConfirmDeleteMessage"
                    values={{ name: questionGroup.name }}
                    components={AppTransComponents}
                    t={t}
                />
            </span>}
            onClose={(result) => {
                if (result) {
                    onUpdate({
                        ...viewData,
                        questionGroups: viewData.questionGroups.filter(x => x.questionGroupId != questionGroup.questionGroupId)
                    });
                }
                setDialog(null);
            }}
        />);
    };

    ///
    const handleAddButton = (option: QuestionActionOptions) => {
        switch (option) {
            case "newQuestion":
                handleEdit(null);
                break;

            case "selectQuestion":
                setDialog(<EditTopicChallengeSelectQuestionsDialog
                    onClose={(questionGroup, questions, groupQuestionTotal) => {
                        if (questionGroup != null && questionGroup.length > 0) {
                            if (!viewData.questionGroups.some(x => x.questionGroupId == questionGroup[0].key)) {
                                const { questionGroups } = viewData;
                                onUpdate({
                                    ...viewData, questionGroups: [...questionGroups, {
                                        questionGroupId: questionGroup[0].key,
                                        name: questionGroup[0].label,
                                        fullName: questionGroup[0].tooltip ?? "",
                                        totalQuestions: groupQuestionTotal ?? 0
                                    }]
                                });
                            } else {
                                enqueueSnackbar(t("TopicChallenge.Edit.Question.GroupAlreadySelectedMessage", { name: questionGroup[0].label }), { variant: "error" });
                            }
                        }

                        if (questions != null) {
                            copyQuestions(questions);
                        }

                        setDialog(null);
                    }}
                />);
                break;

            case "createAI":
                openGenerateQuestions();
                break;
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar a lista
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.questionBank.questionsChallenge({
                    pageNumber: viewFilter.current.pageNumber,
                    pageSize: APP_PAGE_SIZE_DEFAULT,
                    questionGroupId,
                    searchText: viewFilter.current.filter.searchText,
                });
                setQuestions(result.data);
            } catch (e) {
                setQuestions(null);
                setViewError(e);
            }
            setLoading(false);
        }

        if (loading) getData();
    }, [loading]);

    // Ao remover uma questão.
    useEffect(() => {
        async function postData() {
            try {
                if (deleteId) {
                    var result = await api.v1.questionBank.deleteQuestion({ questionId: deleteId });
                    if (result) {
                        enqueueSnackbar(t("Question.List.DeleteSuccessMessage"), { variant: "success" });
                        setLoading(true);
                    }
                    else {
                        enqueueSnackbar(t("Question.List.DeleteErrorMessage"), { variant: "error" });
                    }
                }
            }
            catch (e) {
                MprcRequestSnackbarErrorReport(e);
            }
            finally {
                setDeleteId(null);
            }
        }

        postData();

    }, [deleteId]);

    /// Atualiza total de questões.
    useEffect(() => {
        totalQuestions();
    }, [questions, viewData.questionGroups]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const Filter = () => {
        return (
            <MprcFilterBox minWitdh="100%">
                <MprcFilterLine>
                    <div>
                        <MprcButtonDropdown
                            button={ButtonAdd}
                            text={t("TopicChallenge.Edit.Question.NewQuestionButtonText")}
                            items={getAddOptionsItems()}
                            onChange={handleAddButton}
                        />
                    </div>
                    <div>
                        <IconButtonAI onClick={openGenerateQuestions} />
                    </div>
                </MprcFilterLine>
                <MprcFilterLine>
                    <MprcSearchTextField
                        autoFocus
                        initialValue={viewFilter.current.filter.searchText}
                        confirmMode="debounce"
                        ref={searchTextField}
                        onConfirm={(value) => {
                            viewFilter.current.filter.searchText = value;
                            viewFilter.current.pageNumber = 1;
                            setLoading(true);
                        }}
                        placeholder={t("Question.List.SearchTextPlaceholder")}
                    />
                </MprcFilterLine>
            </MprcFilterBox>
        );
    };

    ///
    const RenderGrid = (
        <>
            <Stack sx={{
                overflowY: "auto",
                height: fullScreenMode
                    ? `calc(100vh - ${hasPagination ? (hasGroups ? "400px" : "370px") : (hasGroups ? "370px" : "340px")})`
                    : hasPagination
                        ? (hasGroups ? "198px" : "230px")
                        : (hasGroups ? "220px" : "260px")
            }} className={AppGlobalClassKeys.thinScrollbar}>
                <Grid container justifyContent="center" alignItems="center" spacing={1} marginTop={1}>
                    {
                        questions?.items.map((item, index) => {
                            return (
                                <Grid key={index} width="100%" size={{ xs: 12 }}>
                                    <Grid container justifyContent="center" alignItems="center" spacing={1} wrap="nowrap">
                                        <Grid size="grow" overflow="hidden">
                                            <Grid container alignItems="center" flexWrap="nowrap" width="100%">
                                                <Grid overflow="hidden">
                                                    <MprcListItemTitle onClick={() => handleEdit(item)}>
                                                        <MprcTextMuted>
                                                            {item.questionText}
                                                        </MprcTextMuted>
                                                    </MprcListItemTitle>
                                                </Grid>
                                                <Grid>
                                                    <MprcIconButtonMoreOptionsDropdown
                                                        color="muted"
                                                        onChange={(selection) => handleSelectQuestionOption(selection, item, index)}
                                                        items={getQuestionMenuItems(item)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            );
                        })
                    }
                </Grid>
            </Stack>
            <Stack justifyContent="center" alignContent="space-between" alignItems="center" width="100%" marginTop={1}>
                <Box>
                    {
                        !loading && questions && questions.totalPages > 1 &&
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={questions.pageNumber}
                            count={questions.totalPages}
                            onChange={(e, pageNumber) => {
                                viewFilter.current.pageNumber = pageNumber;
                                setLoading(true);
                            }}
                        />
                    }
                </Box>
            </Stack>
        </>
    );

    return (
        <>
            {dialog}

            <MprcRequestErrorReport error={viewError} />
            <MprcInfiniteLoading visible={loading} />
            <MprcRequestIndicator
                type={"post"} visible={copying || deleteId != null}
                message={copying ? t("TopicChallenge.Edit.Question.CopyingMessage") : undefined}
            />

            <MprcContainer>

                {Filter()}

                <Box width="100%" marginTop={1}>
                    <MprcRibbonScroll>
                        {
                            viewData.questionGroups.map(item =>
                                <MprcChipChoice
                                    label={`${item.name} (${item.totalQuestions})`}
                                    title={`${item.fullName} (${item.totalQuestions})`}
                                    key={item.questionGroupId}
                                    onClick={() => viewQuestions(item)}
                                    onDelete={() => handleDeleteGroup(item)}
                                />
                            )
                        }
                    </MprcRibbonScroll>
                </Box>

                {RenderGrid}

            </MprcContainer>
        </>
    );
};
